export function formatCurrency(value: string | number, showNegativeInParens = false): string {
  const num = typeof value === 'string' ? parseFloat(value) : value;
  if (!Number.isFinite(num)) return '—';
  const formatted = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Math.abs(num));
  if (num < 0) {
    return showNegativeInParens ? `(${formatted})` : `-${formatted}`;
  }
  return formatted;
}

export function formatPercent(value: number, decimals = 1): string {
  if (!Number.isFinite(value)) return '—';
  const sign = value >= 0 ? '+' : '';
  return `${sign}${value.toFixed(decimals)}%`;
}

export function formatAccountingDate(isoDate: string): string {
  if (!isoDate) return '';
  const d = new Date(isoDate + (isoDate.length === 10 ? 'T00:00:00' : ''));
  if (Number.isNaN(d.getTime())) return isoDate;
  return d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

export function companyAddressLines(
  c:
    | {
        addressLine1?: string | null;
        addressLine2?: string | null;
        city?: string | null;
        region?: string | null;
        postalCode?: string | null;
        phone?: string | null;
      }
    | null
    | undefined,
): string[] {
  if (!c) return [];
  const lines: string[] = [];
  if (c.addressLine1) lines.push(c.addressLine1);
  if (c.addressLine2) lines.push(c.addressLine2);
  const cityLine = [c.city, [c.region, c.postalCode].filter(Boolean).join(' ').trim()]
    .filter(Boolean)
    .join(', ');
  if (cityLine) lines.push(cityLine);
  if (c.phone) lines.push(c.phone);
  return lines;
}

// Words that should display fully upper-cased rather than title-cased.
const LABEL_ACRONYMS = new Set(['cogs', 'gl', 'ar', 'ap', 'ein', 'usd', 'ach', 'eom']);

/**
 * Turn a machine label (snake_case / lowercase) into a human one for display.
 * "accounts_receivable" -> "Accounts Receivable", "depository" -> "Depository",
 * "cogs" -> "COGS". Display-only; never use the result as a key.
 */
export function humanizeLabel(value: string | null | undefined): string {
  if (!value) return '';
  return value
    .split(/[_\s]+/)
    .filter(Boolean)
    .map((w) =>
      LABEL_ACRONYMS.has(w.toLowerCase())
        ? w.toUpperCase()
        : w.charAt(0).toUpperCase() + w.slice(1).toLowerCase(),
    )
    .join(' ');
}

/** Exact sum of NUMERIC(19,4) money strings via integer (BigInt) math — never float. */
export function sumMoneyStrings(values: string[]): string {
  let total = 0n;
  for (const raw of values) {
    const v = (raw ?? '0').trim();
    const neg = v.startsWith('-');
    const [intPart, fracPart = ''] = v.replace('-', '').split('.');
    const frac = (fracPart + '0000').slice(0, 4);
    const scaled = BigInt((intPart || '0') + frac);
    total += neg ? -scaled : scaled;
  }
  const neg = total < 0n;
  const abs = (neg ? -total : total).toString().padStart(5, '0');
  return `${neg ? '-' : ''}${abs.slice(0, -4)}.${abs.slice(-4)}`;
}

/** Negate a money string. */
export function negateMoney(value: string): string {
  const v = (value ?? '0').trim();
  if (/^-?0(\.0+)?$/.test(v)) return v.replace('-', '');
  return v.startsWith('-') ? v.slice(1) : `-${v}`;
}

/**
 * Format `amount / anchor * 100` as a "12.5%" string. Returns "" when the
 * anchor is zero or either side parses to NaN. Used by reports to show each
 * line as a % of total revenue (IS) or total assets (BS).
 */
export function formatPctOfTotal(amount: string | number, anchor: string | number): string {
  const a = typeof amount === 'number' ? amount : parseFloat(amount);
  const t = typeof anchor === 'number' ? anchor : parseFloat(anchor);
  if (!isFinite(a) || !isFinite(t) || t === 0) return '';
  const pct = (a / t) * 100;
  // Cap absurd values (e.g. tiny anchor) so the column doesn't blow up.
  if (!isFinite(pct) || Math.abs(pct) > 99_999) return '';
  return `${pct.toFixed(1)}%`;
}

export function todayIso(): string {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${dd}`;
}
